/**
 * 零配置模式
 * 智能默认值，最少配置即可启动
 */
import type { RemoteKitClientOptions } from './client'

/** 零配置选项（仅必填项） */
export interface ZeroConfigOptions {
  /** 服务端地址 */
  serverUrl: string
  /** 认证令牌 */
  authToken: string
}

/**
 * 从环境变量自动推断配置
 */
export function configFromEnv(): Partial<RemoteKitClientOptions> {
  return {
    serverUrl: process.env.REMOTEKIT_SERVER_URL || process.env.SERVER_URL || '',
    authToken: process.env.REMOTEKIT_AUTH_TOKEN || process.env.AUTH_TOKEN || '',
    clientType: process.env.REMOTEKIT_CLIENT_TYPE || process.env.CLIENT_TYPE || 'auto',
  }
}

/**
 * 智能默认配置
 * 根据环境自动选择最佳参数
 */
export function smartDefaults(partial: ZeroConfigOptions): RemoteKitClientOptions {
  const isProduction = process.env.NODE_ENV === 'production'
  const isSecure = partial.serverUrl.startsWith('wss://') || partial.serverUrl.startsWith('https://')

  return {
    serverUrl: partial.serverUrl,
    authToken: partial.authToken,
    clientType: process.env.REMOTEKIT_CLIENT_TYPE || 'auto',
    transports: isSecure ? ['websocket'] : ['websocket', 'polling'],
    reconnection: {
      enabled: true,
      maxAttempts: isProduction ? 30 : 10,
      initialDelay: 1000,
      maxDelay: isProduction ? 30000 : 5000,
      factor: 2,
    },
    enableShell: false,
    heartbeatInterval: isProduction ? 30000 : 60000,
  }
}

/**
 * 合并用户配置和默认值
 */
export function mergeConfig(
  userConfig: Partial<RemoteKitClientOptions> & ZeroConfigOptions,
): RemoteKitClientOptions {
  const defaults = smartDefaults(userConfig)
  return { ...defaults, ...userConfig }
}

/**
 * 检测运行环境
 */
export function detectEnvironment(): {
  runtime: 'node' | 'browser' | 'worker' | 'deno'
  isProduction: boolean
  platform: string
  version: string
} {
  const isNode = typeof process !== 'undefined' && process.versions?.node
  const isDeno = typeof (globalThis as any).Deno !== 'undefined'
  const isWorker = typeof globalThis !== 'undefined' && typeof (globalThis as any).importScripts === 'function'
  const isBrowser = typeof globalThis !== 'undefined' && typeof (globalThis as any).window !== 'undefined'

  let runtime: 'node' | 'browser' | 'worker' | 'deno' = 'node'
  if (isDeno) runtime = 'deno'
  else if (isWorker) runtime = 'worker'
  else if (isBrowser) runtime = 'browser'

  return {
    runtime,
    isProduction: process.env.NODE_ENV === 'production',
    platform: isNode ? process.platform : 'unknown',
    version: isNode ? process.versions.node : 'unknown',
  }
}
